import { Breadcrumb, Button, Tabs, Typography, message } from "antd";
import { Link, useLoaderData, useSearchParams } from "react-router-dom";
import { useRouteActivity } from "~/hooks/useRouteActivity";
import { OrganizationDetailsTab, JoinRequestsTab } from "./components";
import { OrganizationDetailsPageLoaderType } from "./loaders";
import { useTranslation } from "react-i18next";
import {
  breadcrumbHomeItem,
  breadcrumbLinkItem,
  CustomTable,
  TableHeaderBar,
  TableHeaderInfo,
} from "~/components";
import { ColumnsType } from "antd/es/table";
import * as APITypes from "~/API";
import { TFunction } from "i18next";

export const getOrganizationMembersTableColumns = (
  t: TFunction<"translation">,
): ColumnsType<APITypes.User> => [
  {
    title: t("Username"),
    dataIndex: "username",
    key: "username",
    width: 200,
    render: (username: string, record: APITypes.User) => (
      <Link
        to={`/organization/${record.organizationID}/member/${record.id}?tab=details`}
        style={{ fontWeight: 500 }}
      >
        {username}
      </Link>
    ),
  },
  {
    title: t("First_Name"),
    dataIndex: "firstName",
    key: "firstName",
    width: 150,
  },
  {
    title: t("Last_Name"),
    dataIndex: "lastName",
    key: "lastName",
    width: 150,
  },
  {
    title: t("Email"),
    dataIndex: "email",
    key: "email",
    width: 200,
  },
  {
    title: t("Title"),
    dataIndex: "title",
    key: "title",
    width: 150,
  },
  {
    title: t("Number"),
    dataIndex: "phoneNumber",
    key: "phoneNumber",
    width: 150,
  },
  {
    title: t("Role"),
    dataIndex: "roles",
    key: "roles",
    width: 150,
  },
  {
    title: t("Actions"),
    key: "actions",
    width: 120,
    render: (_, record: APITypes.User) => (
      <Button
        size="small"
        danger
        onClick={() => {
          message.info("Remove member: To be implemented");
        }}
      >
        {t("Delete")}
      </Button>
    ),
  },
];

export const OrganizationDetailsPage = () => {
  const { t } = useTranslation();
  const loaderData = useLoaderData() as OrganizationDetailsPageLoaderType;
  const { isTableLoading } = useRouteActivity();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get current tab from URL, default to "details"
  const currentTab = searchParams.get("tab") || "details";

  // Handle tab change - update URL
  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key });
  };

  // Handle invite - copy organization ID
  const handleCopyOrgId = () => {
    navigator.clipboard.writeText(loaderData.organizationDetails.id);
    message.success(t("Organization_ID_Copied"));
  };

  return (
    <>
      <Breadcrumb
        separator=">"
        data-tour="org-details-breadcrumb"
        items={[
          breadcrumbHomeItem,
          breadcrumbLinkItem(
            `/organization/${loaderData.organizationDetails.id}`,
            `${t("Organization")}: ${loaderData.organizationDetails.id}`,
          ),
        ]}
      />

      <Typography.Title level={1} data-tour="org-details-header">
        {t("Organization")}: {loaderData.organizationDetails.name}
      </Typography.Title>

      <Tabs
        activeKey={currentTab}
        onChange={handleTabChange}
        data-tour="org-details-tabs"
        items={[
          {
            label: t("Details"),
            key: "details",
            children: (
              <OrganizationDetailsTab
                organizationDetails={loaderData.organizationDetails}
                canEditOrganization={loaderData.canEditOrganization}
                canDeleteOrganization={loaderData.canDeleteOrganization}
              />
            ),
          },
          {
            label: (
              <Typography.Text>
                {t("Members")} ({loaderData.membersList.length})
              </Typography.Text>
            ),
            key: "members",
            children: (
              <div data-tour="org-members-tab-content">
                <div data-tour="org-members-rows">
                  <CustomTable
                    tableHeaders={
                      <TableHeaderBar
                        left={
                          <TableHeaderInfo
                            title={t("All_Members")}
                            count={loaderData.membersList.length}
                            countLabel={t("Total_Records_Found")}
                          />
                        }
                        right={
                          <Button
                            type="primary"
                            ghost
                            onClick={handleCopyOrgId}
                            data-tour="org-invite-button"
                          >
                            {t("Invite_Member")}
                          </Button>
                        }
                      />
                    }
                    tableProps={{
                      dataSource: loaderData.membersList || [],
                      columns: getOrganizationMembersTableColumns(t),
                      loading: isTableLoading,
                    }}
                  />
                </div>
              </div>
            ),
          },
          {
            label: (
              <Typography.Text>
                {t("Join_Requests")} ({loaderData.joinRequests.length})
              </Typography.Text>
            ),
            key: "join-requests",
            children: (
              <div data-tour="org-join-requests-tab-content">
                <JoinRequestsTab joinRequests={loaderData.joinRequests} />
              </div>
            ),
          },
        ]}
      />
    </>
  );
};
